import { Button } from "@/components/ui/button";
import { Copy, Share2, Facebook, MessageCircle, MessageSquare, Image as ImageIcon, Linkedin, Loader2 } from "lucide-react";
import { XIcon } from "@/components/ui/icons/XIcon";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { 
  generateShareText, 
  generateShareUrl, 
  generateTwitterUrl,
  generateFacebookUrl,
  generateLinkedInUrl,
  generateWhatsAppUrl,
  generateSMSUrl,
  copyToClipboard,
  getPlatformShareCapabilities,
  type ShareOptions 
} from "@/utils/shareEngine";
import { generateShareImage, downloadShareImage, shareGeneratedImage } from "@/utils/shareImageGenerator";
import { AICaptionGenerator } from "./AICaptionGenerator";
import { trackShareEvent } from "@/utils/shareAnalytics";
import { ShareTroubleshoot } from "./ShareTroubleshoot";

interface ViralCTABarProps {
  shareOptions: ShareOptions;
}

export function ViralCTABar({ shareOptions }: ViralCTABarProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [customCaption, setCustomCaption] = useState("");
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const capabilities = getPlatformShareCapabilities();

  const getShareTextWithCaption = () => {
    const baseText = generateShareText(shareOptions);
    if (customCaption) {
      return `${customCaption}\n\n${baseText}`;
    }
    return baseText;
  };

  const handleCopy = async () => {
    const shareText = getShareTextWithCaption();
    const success = await copyToClipboard(shareText);
    
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      trackShareEvent('copy', {
        puzzleNumber: shareOptions.puzzleNumber,
        won: shareOptions.won || false,
        attempts: shareOptions.attempts || 0,
        hadCaption: !!customCaption,
      });
      
      toast({
        title: "Copied to clipboard!",
        description: "Share your results with friends",
      });
    } else {
      toast({
        title: "Copy failed",
        description: "Could not copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleNativeShare = async () => {
    const shareText = getShareTextWithCaption();
    const shareUrl = generateShareUrl(shareOptions.puzzleNumber);
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: "WordPop — Daily Word Puzzle",
          text: shareText,
          url: shareUrl,
        });
        
        trackShareEvent('native_share', {
          puzzleNumber: shareOptions.puzzleNumber,
          won: shareOptions.won || false,
          attempts: shareOptions.attempts || 0,
          hadCaption: !!customCaption,
        });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          handleCopy();
        }
      }
    } else {
      handleCopy();
    }
  };

  const handleTwitterShare = () => {
    const shareText = getShareTextWithCaption();
    const shareUrl = generateShareUrl(shareOptions.puzzleNumber);
    const twitterUrl = generateTwitterUrl(shareText, shareUrl);
    
    trackShareEvent('twitter', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.open(twitterUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  };

  const handleFacebookShare = () => {
    const shareUrl = generateShareUrl(shareOptions.puzzleNumber);
    const facebookUrl = generateFacebookUrl(shareUrl);
    
    // Log version for debugging stale cache issues
    console.log('[Share] Facebook share triggered, version:', import.meta.env.VITE_BUILD_HASH || 'dev');
    console.log('[Share] Facebook URL:', facebookUrl);
    
    // Verify we're using the correct sharer.php endpoint (not dialog.share)
    if (!facebookUrl.includes('sharer/sharer.php')) {
      console.error('[Share] ⚠️ Stale code detected! Facebook URL should use sharer.php');
      toast({
        title: "Update required",
        description: "Please tap 'Update' below to fix Facebook sharing",
        variant: "destructive",
      });
      return;
    }
    
    trackShareEvent('facebook', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    // Use a different approach for iOS to avoid Facebook app interception
    // which causes "Internal Facebook App Misconfigured" error
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (isIOS) {
      // On iOS, copy the URL and show instructions
      copyToClipboard(shareUrl);
      toast({
        title: "Link copied!",
        description: "Open Facebook and paste to share (iOS limitation)",
      });
    } else {
      window.open(facebookUrl, "_blank", "noopener,noreferrer,width=550,height=420");
    }
  };

  const handleLinkedInShare = () => {
    const shareUrl = generateShareUrl(shareOptions.puzzleNumber);
    const linkedInUrl = generateLinkedInUrl(shareUrl);
    
    trackShareEvent('linkedin', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.open(linkedInUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  };

  const handleWhatsAppShare = () => {
    const shareText = getShareTextWithCaption();
    const whatsappUrl = generateWhatsAppUrl(shareText);
    
    trackShareEvent('whatsapp', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const handleSMSShare = () => {
    const shareText = getShareTextWithCaption();
    const smsUrl = generateSMSUrl(shareText);
    
    trackShareEvent('sms', {
      puzzleNumber: shareOptions.puzzleNumber,
      won: shareOptions.won || false,
      attempts: shareOptions.attempts || 0,
      hadCaption: !!customCaption,
    });
    
    window.location.href = smsUrl;
  };

  const handleImageShare = async () => {
    if (!shareOptions.results || shareOptions.attempts === undefined) {
      toast({
        title: "Cannot generate image",
        description: "Game results not available",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingImage(true);
    try {
      const imageDataUrl = await generateShareImage({
        puzzleNumber: shareOptions.puzzleNumber,
        won: shareOptions.won || false,
        attempts: shareOptions.attempts,
        results: shareOptions.results,
      });

      // Try native share first
      const shared = await shareGeneratedImage(imageDataUrl, {
        puzzleNumber: shareOptions.puzzleNumber,
        won: shareOptions.won || false,
        attempts: shareOptions.attempts,
        results: shareOptions.results,
      });

      trackShareEvent('image', {
        puzzleNumber: shareOptions.puzzleNumber,
        won: shareOptions.won || false,
        attempts: shareOptions.attempts,
        hadCaption: !!customCaption,
      });

      if (!shared) {
        // Fallback to download
        downloadShareImage(imageDataUrl, `wordpop-${shareOptions.puzzleNumber}.png`);
        toast({
          title: "Image downloaded!",
          description: "Share it on your favorite social platform",
        });
      }
    } catch (err) {
      toast({
        title: "Failed to generate image",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Pre-share update check with version indicator */}
      <ShareTroubleshoot showVersion />
      
      {shareOptions.attempts !== undefined && (
        <AICaptionGenerator
          guesses={shareOptions.attempts}
          won={shareOptions.won || false}
          streak={shareOptions.currentStreak || 0}
          puzzleNumber={shareOptions.puzzleNumber}
          onCaptionGenerated={setCustomCaption}
        />
      )}
      
      <div className="flex flex-wrap gap-2">
        <Button 
          onClick={handleCopy} 
          variant="outline" 
          size="sm"
          className="flex-1 min-w-[100px]"
          aria-label="Copy results to clipboard"
        >
          <Copy className="h-4 w-4 mr-1" />
          {copied ? "Copied!" : "Copy"}
        </Button>
        
        <Button 
          onClick={handleWhatsAppShare} 
          variant="outline" 
          size="sm"
          className="flex-1 min-w-[100px]"
          aria-label="Share on WhatsApp"
        >
          <MessageCircle className="h-4 w-4 mr-1" />
          WhatsApp
        </Button>

        <Button 
          onClick={handleSMSShare} 
          variant="outline" 
          size="sm"
          className="flex-1 min-w-[100px]"
          aria-label="Share via SMS"
        >
          <MessageSquare className="h-4 w-4 mr-1" />
          SMS
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button 
          onClick={handleTwitterShare} 
          variant="outline" 
          size="sm"
          className="flex-1 min-w-[80px]"
          aria-label="Share on X"
        >
          <XIcon className="h-4 w-4 mr-1" />
          X
        </Button>

        <Button 
          onClick={handleFacebookShare} 
          variant="outline" 
          size="sm"
          className="flex-1 min-w-[80px]"
          aria-label="Share on Facebook"
        >
          <Facebook className="h-4 w-4 mr-1" />
          Facebook
        </Button>

        <Button 
          onClick={handleLinkedInShare} 
          variant="outline" 
          size="sm"
          className="flex-1 min-w-[80px]"
          aria-label="Share on LinkedIn"
        >
          <Linkedin className="h-4 w-4 mr-1" />
          LinkedIn
        </Button>

        {shareOptions.results && (
          <Button 
            onClick={handleImageShare} 
            variant="outline" 
            size="sm"
            className="flex-1 min-w-[80px]"
            aria-label="Generate share image"
            disabled={isGeneratingImage}
          >
            {isGeneratingImage ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <ImageIcon className="h-4 w-4 mr-1" />
            )}
            Image
          </Button>
        )}
      </div>

      {capabilities.hasNativeShare && (
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={handleNativeShare} 
            variant="outline" 
            size="sm"
            className="w-full"
            aria-label="Share via native share menu"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      )}
    </div>
  );
}
